import CheckboxPresenter from "../presenter/checkbox.js";

import CheckboxesView from "../view/checkboxes.js";
import TriggerView from "../view/checkboxes/trigger.js";

import TriggerModel from "../model/checkboxes/trigger.js";
import CheckboxesModel from "../model/checkboxes/checkboxes";

import {
  render
} from "../../../render/render.js";

import {
  isInViewport
} from "../../../../utils/is-in-viewport.js";

import {
  VISIBLE_CHECKBOXES_NUMBER,
  defaultCheckboxesTriggerModelData,
  defaultCheckboxesModelData,
  ActionType,
  PROPERTIES_SEPARATOR,
  CHECKBOX_ANIMATION_DURATION
} from "../../const.js";

export default class Checkboxes {
  constructor(
      element,
      scrollToElement,
      animation,
      resetPresenter,
      browserNavigation,
      isActive
  ) {
    this._element = element;
    this._scrollToElement = scrollToElement;
    this._animation = animation;
    this._resetPresenter = resetPresenter;
    this._browserNavigation = browserNavigation;
    this._isActive = isActive;

    this._checkboxesPresenters = [];

    this._checkboxesView = null;
    this._triggerView = null;

    this._triggerModel = null;
    this._checkboxesModel = null;

    this._relativeScrollContainer = null;

    this._handleTriggerClick = this._handleTriggerClick.bind(this);
    this._handleCheckboxesButtonClick = this._handleCheckboxesButtonClick.bind(this);
    this._handleChangeModel = this._handleChangeModel.bind(this);
    this._afterResetClick = this._afterResetClick.bind(this);
    this._handleBrowserNavigation = this._handleBrowserNavigation.bind(this);
  }

  init() {
    this._checkboxesView = new CheckboxesView(this._element);

    const checkboxesElements = this._checkboxesView.getCheckboxesElements();

    Array.from(checkboxesElements).forEach((item) => {
      const presenter = new CheckboxPresenter(item, this._animation);
      presenter.init();

      this._checkboxesPresenters.push(presenter);
    });

    this._initCheckboxesModel();
    this._checkboxesView.setButtonClickHandler(this._handleCheckboxesButtonClick);

    this._setCheckboxesViews();

    this._resetPresenter.addObserver(this._afterResetClick);
    this._resetPresenter.addItem(`checkboxes`, this);

    this._browserNavigation.addObserver(this._handleBrowserNavigation);
  }

  getName() {
    if (this._checkboxesPresenters.length === 0) {
      return null;
    }

    return this._checkboxesPresenters[0].getName();
  }

  getModelData() {
    return this._checkboxesModel.getData();
  }

  setRelativeScrollContainer(container) {
    this._relativeScrollContainer = container;
  }

  removeRelativeScrollContainer() {
    this._relativeScrollContainer = null;
  }

  disableValue(value) {
    this._checkboxesPresenters.forEach((presenter) => {
      if (value !== presenter.getValue()) {
        return;
      }

      presenter.reset();
    });
  }

  reset() {
    this._checkboxesPresenters.forEach((presenter) => presenter.reset());

    if (this._checkboxesModel.getData().isTitleActive) {
      this._checkboxesModel.changeData(
          Object.assign(
              {},
              this._checkboxesModel.getData(),
              {
                isTitleActive: false,
                isSomeChecked: false
              }
          ), ActionType.DEACTIVATE_FIELDSET_TITLE
      );
    }
  }

  updateEnabledValues(data) {
    this._checkboxesPresenters.forEach((presenter) => {
      presenter.updateEnabledValues(data);
    });

    const isSomeChecked = this._checkboxesView.checkIfSomeChecked();

    if (isSomeChecked && !this._checkboxesModel.getData().isTitleActive) {
      this._checkboxesModel.changeData(
          Object.assign(
              {},
              this._checkboxesModel.getData(),
              {
                isTitleActive: true,
                isSomeChecked
              }
          ), ActionType.ACTIVATE_FIELDSET_TITLE
      );
    }
  }

  _initCheckboxesModel() {
    const isSomeChecked = this._checkboxesPresenters
      .slice()
      .some((view) => view.checkIfChecked());

    this._isActive = (isSomeChecked) ? true : this._isActive;
    this._checkboxesModel = new CheckboxesModel(Object.assign(
        {},
        defaultCheckboxesModelData,
        {
          isActive: this._isActive,
          isTitleActive: isSomeChecked,
          isSomeChecked
        }
    ));
    this._checkboxesModel.addObserver(this._handleChangeModel);

    if (!this._checkboxesModel.getData().isActive) {
      this._checkboxesModel.changeData(Object.assign(
          {},
          this._checkboxesModel.getData()
      ), ActionType.DEACTIVATE_FIELDSET);
    }
  }

  _processFieldsetActivate() {
    this._animation.slideDown(
        this._checkboxesView.getFieldsetContent(),
        CHECKBOX_ANIMATION_DURATION,
        null,
        () => {
          this._checkboxesView.resetFieldsetContent();
        }
    );

    this._checkboxesView.activate();
  }

  _processFieldsetDeactivate() {
    this._animation.slideUp(
        this._checkboxesView.getFieldsetContent(),
        CHECKBOX_ANIMATION_DURATION,
        null,
        () => {
          this._checkboxesView.hideFieldsetContent();
        }
    );

    this._checkboxesView.deactivate();
  }

  _processFieldsetActivateTitle() {
    this._checkboxesView.activateTitle();
  }

  _processFieldsetDeactivateTitle() {
    this._checkboxesView.deactivateTitle();
  }

  _initTrigger(hiddenCount, isExpanded) {
    this._triggerModel = new TriggerModel(
        Object.assign(
            {},
            defaultCheckboxesTriggerModelData,
            {
              hiddenCount,
              expanded: isExpanded
            }
        )
    );
    this._triggerView = new TriggerView(hiddenCount, isExpanded);
    const triggerElement = this._triggerView.getElement();
    render(this._checkboxesView.getFieldsetContent(), triggerElement);

    this._triggerModel.addObserver(this._handleChangeModel);
    this._triggerView.setClickHandler(this._handleTriggerClick);
  }

  _afterResetClick() {
    this._checkboxesPresenters.forEach((presenter) => {
      presenter.reset();
    });
  }

  _setCheckboxesViews() {
    const checkboxesElements = this._checkboxesView.getCheckboxesElements();
    const isSomeChecked = this._checkboxesView.checkIfSomeChecked();

    let hiddenCount = 0;

    this._checkboxesPresenters.forEach((presenter, index) => {
      const hidden = (index >= VISIBLE_CHECKBOXES_NUMBER && !isSomeChecked) ? true : false;

      if (hidden) {
        presenter.hide();
      }

      if (index >= VISIBLE_CHECKBOXES_NUMBER) {
        hiddenCount += 1;
      }
    });

    if (checkboxesElements.length > VISIBLE_CHECKBOXES_NUMBER) {
      this._initTrigger(hiddenCount, isSomeChecked);
    }
  }

  _handleTriggerClick() {
    const modelData = this._triggerModel.getData();
    const action = (modelData.expanded) ? ActionType.SHRINK : ActionType.EXPAND;

    this._triggerModel.changeData(
        Object.assign(
            {},
            modelData,
            {
              expanded: !modelData.expanded
            }
        ),
        action
    );
  }

  _handleCheckboxesButtonClick() {
    const modelData = this._checkboxesModel.getData();
    const action = (modelData.isActive) ?
      ActionType.DEACTIVATE_FIELDSET :
      ActionType.ACTIVATE_FIELDSET;

    this._checkboxesModel.changeData(Object.assign(
        {},
        modelData,
        {
          isActive: !modelData.isActive
        }
    ), action);
  }

  _handleBrowserNavigation(params) {
    const name = this._checkboxesPresenters[0].getName();
    const checkedValues = params
      .filter((item) => {
        return item.name === name;
      })
      .map((item) => {
        return item.values
          .split(PROPERTIES_SEPARATOR)
          .map((value) => decodeURIComponent(value));
      })
      .flat()
      .map((item) => {
        return item.trim();
      });

    this._checkboxesPresenters.forEach((presenter) => {
      const value = presenter.getValue();

      if (checkedValues.indexOf(value) !== -1) {
        presenter.check();
      } else {
        presenter.reset();
      }
    });
  }

  _handleChangeModel(action) {
    switch (action) {
      case ActionType.EXPAND:
        this._checkboxesPresenters.forEach((presenter, index) => {
          if (index >= VISIBLE_CHECKBOXES_NUMBER) {
            presenter.show();
          }
        });
        this._triggerView.setExpanded();
        break;
      case ActionType.SHRINK:
        this._checkboxesPresenters.forEach((presenter, index) => {
          if (index >= VISIBLE_CHECKBOXES_NUMBER) {
            presenter.hide();
          }
        });
        this._triggerView.setShrink();

        const titleElement = this._checkboxesView.getTitleElement();

        const scrollOptions = {
          element: this._element
        };

        if (this._relativeScrollContainer) {
          scrollOptions.relativeContainer = this._relativeScrollContainer;
        }

        if (!isInViewport(titleElement)) {
          this._scrollToElement.scroll(scrollOptions);
        }
        break;
      case ActionType.ACTIVATE_FIELDSET:
        this._processFieldsetActivate();
        break;
      case ActionType.DEACTIVATE_FIELDSET:
        this._processFieldsetDeactivate();
        break;
      case ActionType.DEACTIVATE_FIELDSET_TITLE:
        this._processFieldsetDeactivateTitle();
        break;
      case ActionType.ACTIVATE_FIELDSET_TITLE:
        this._processFieldsetActivateTitle();
        break;
    }
  }
}
